import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import successStartGuide1 from '../../assets/images/successStartGuide1.png';
import successStartGuide2 from '../../assets/images/successStartGuide2.png';
import successStartGuide3 from '../../assets/images/successStartGuide3.png';
import successStartGuide4 from '../../assets/images/successStartGuide4.png';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {RandomImage} from '../common/randomImage';

const adminSuccessImages = [successStartGuide1, successStartGuide2];
const successImages = [successStartGuide3, successStartGuide4];

/*
 * Props.
 */

interface StartGuideTaskSuccessProps {
  onDismiss: Handler;
  isAdmin: boolean;
}

/*
 * Style.
 */

const StyledSuccessWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: ${(p) => p.theme.greys.shade10};
  padding: 7px 7px 20px;
  margin-bottom: 30px;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledSuccessHeaderDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  line-height: 25px;
  padding-top: 22px;
`;

const StyledSuccessSubheaderDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  line-height: 18px;
  text-align: center;
`;

const StyledSuccessHeaderButtonsDiv = styled.div`
  display: flex;
  flex-direction: row-reverse;
  width: 100%;
`;

/*
 * Component.
 */

export const StartGuideTaskSuccess: FC<StartGuideTaskSuccessProps> = (props) => {
  const theme = useUserTheme();
  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_TASKS_COMPLETED, {
      user_track: props.isAdmin ? 'admin' : 'non_admin',
    });
  });

  return (
    <StyledSuccessWrapperDiv>
      <StyledSuccessHeaderButtonsDiv>
        <IconButton
          label=""
          iconName="closeSmall"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          backgroundHoverColor={theme.alphas.gray10}
          onClick={props.onDismiss}
        />
      </StyledSuccessHeaderButtonsDiv>
      <RandomImage
        urls={props.isAdmin ? adminSuccessImages : successImages}
        height={148}
        width={160}
        alt=""
      />
      <StyledSuccessHeaderDiv>
        {props.isAdmin ? (
          <Trans id="task-success-header-admin">All done!</Trans>
        ) : (
          <Trans id="task-success-header">You did it!</Trans>
        )}
      </StyledSuccessHeaderDiv>
      <StyledSuccessSubheaderDiv>
        {props.isAdmin ? (
          <Trans id="task-success-subheader-admin">
            Congrats on learning
            <br />
            your way around Front.
          </Trans>
        ) : (
          <Trans id="task-success-subheader">
            You&apos;re all set to get the
            <br />
            best out of Front!
          </Trans>
        )}
      </StyledSuccessSubheaderDiv>
    </StyledSuccessWrapperDiv>
  );
};
